import { type ReactNode } from "react";

import { Link } from "react-router-dom";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import { cx, styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Icon, Popup, type SemanticICONS } from "simplydo/react/ui";

import type { SideNavComponentCollapsibleProps } from "./SideNavComponent";

export type SideNavLinkProps = {
  to?: string;
  external?: boolean;
  showExternalIcon?: boolean;
  onClick?: () => void;
  content: ReactNode;
  tooltip?: ReactNode;
  iconLeft?: SemanticICONS | ReactNode;
  iconLeftAlt?: ReactNode;
  iconRight?: SemanticICONS | ReactNode;
  active?: boolean;
  compact?: boolean | "very";
  inset?: boolean;
  completed?: "done" | "almost";
  rightIcon?: any;
  secondary?: boolean;
  oneLine?: boolean;
  style?: React.CSSProperties;
};

const StyledSideNavLink = styled.div`
  &.is-collapsed {
    margin: 0 !important;
  }

  .sidebar-nav-link {
    cursor: pointer;
    display: flex;
    flex-direction: row;
    align-items: center;

    > div.content {
      display: flex;
      flex: 1;
    }

    > div.content.is-one-line {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    > .icons-right,
    .icons-left {
      display: inline-flex;
      align-items: center;
      flex: 0;
      min-height: 20px;
    }
    text-transform: capitalize;
    justify-content: space-between;
    padding-top: 10px;
    padding-left: 10px;
    padding-right: 10px;
    padding-bottom: 10px;
    border-radius: 5px;

    &.is-inset {
      width: calc(100% - 30px);
      margin-left: 30px;
    }

    &:not(.is-inset) {
      width: 100%;
      margin-left: auto;
    }

    &.is-compact-very {
      font-size: 13.5px;
    }

    &:not(.is-compact-very) {
      font-size: 15px;
    }

    &.is-active {
      background-color: #d2dde5;
    }

    &:not(.is-active) {
      background-color: none;
    }

    &:hover {
      background-color: #eef1f2;
    }

    &.is-active:hover {
      background-color: #d2dde5;
    }

    .icon-alt {
      margin: 0 15px 0 2.5px;
    }

    > * {
      display: block;
    }

    > * {
      color: black !important;
      font-weight: normal;
    }

    &.is-secondary > * {
      color: gray !important;
    }

    &.is-active > * {
      font-weight: bold;
    }
  }
`;

const createIconComponent = (
  icon: SemanticICONS | ReactNode,
  isCollapsed = false,
) => {
  if (!icon) {
    return null;
  }
  if (typeof icon === "string") {
    return (
      <Icon
        style={{ marginRight: isCollapsed ? 0 : 10, height: 19 }}
        name={icon as SemanticICONS}
      />
    );
  }
  return icon;
};

const SideNavLinkContainerComponent = ({
  children,
  external,
  to,
  onClick,
}: {
  children: ReactNode;
  external?: boolean;
  to?: string;
  onClick?: () => void;
}) => {
  if (to) {
    if (external) {
      return (
        <a href={to} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    }
    return (
      <Link to={to} onClick={onClick}>
        {children}
      </Link>
    );
  }
  return (
    <div
      role="link"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleClickForKeyboard(onClick)}
    >
      {children}
    </div>
  );
};

const colorMap = { done: "green", almost: "orange" } as const;

const SideNavLink = ({
  to,
  content,
  tooltip,
  iconLeft,
  iconLeftAlt,
  iconRight,
  active,
  onClick,
  compact,
  inset,
  completed,
  external,
  showExternalIcon,
  rightIcon,
  isCollapsed,
  secondary,
  oneLine,
  style,
}: SideNavLinkProps & Partial<SideNavComponentCollapsibleProps>) => {
  const theme = useTheme();

  return (
    <Popup
      disabled={!theme.sizes.isComputer || (!tooltip && !isCollapsed)}
      position="right center"
      hoverable={!!tooltip}
      on={["hover", "focus"]}
      content={tooltip || content}
      trigger={
        <StyledSideNavLink
          className={cx(
            theme.sizes.isComputer && isCollapsed && "is-collapsed",
          )}
        >
          <SideNavLinkContainerComponent
            external={external}
            to={to}
            onClick={onClick}
          >
            <div
              className={cx(
                "sidebar-nav-link",
                active && "is-active",
                compact === "very" && "is-compact-very",
                inset && "is-inset",
                secondary && "is-secondary",
              )}
              style={style}
            >
              <div className="icons-left">
                {iconLeftAlt ?
                  <span className="icon-alt">{iconLeftAlt}</span>
                : createIconComponent(iconLeft, isCollapsed)}
              </div>
              <div className={cx("content", oneLine && "is-one-line")}>
                {!(theme.sizes.isComputer && isCollapsed) ? content : null}
              </div>
              {!(theme.sizes.isComputer && isCollapsed) ?
                <div className="icons-right">
                  {external || showExternalIcon ?
                    <Icon
                      name="external alternate"
                      size="small"
                      style={{ marginLeft: 5, height: 17 }}
                    />
                  : null}{" "}
                  {completed !== undefined && (
                    <Icon
                      name={
                        completed === "done" ? "check circle" : (
                          "exclamation circle"
                        )
                      }
                      style={{ marginLeft: 5, height: 20 }}
                      color={colorMap[completed] || "red"}
                    />
                  )}{" "}
                  {rightIcon && rightIcon}
                  {createIconComponent(iconRight)}
                </div>
              : null}
            </div>
          </SideNavLinkContainerComponent>
        </StyledSideNavLink>
      }
    />
  );
};

export default SideNavLink;
